/**
 * Traccia il caricamento degli asset critici visibili dopo il preloader.
 */

function loadImage(src) {
  return new Promise(function (resolve) {
    if (!src) {
      resolve();
      return;
    }

    var img = new Image();

    img.onload = function () {
      resolve();
    };

    img.onerror = function () {
      resolve();
    };

    img.src = src;
  });
}

function loadVideoElement(video) {
  return new Promise(function (resolve) {
    if (!video) {
      resolve();
      return;
    }

    if (video.readyState >= 3) {
      resolve();
      return;
    }

    var settled = false;

    function done() {
      if (settled) {
        return;
      }

      settled = true;
      video.removeEventListener('canplaythrough', done);
      video.removeEventListener('loadeddata', done);
      video.removeEventListener('error', done);
      resolve();
    }

    video.addEventListener('canplaythrough', done, { once: true });
    video.addEventListener('loadeddata', done, { once: true });
    video.addEventListener('error', done, { once: true });

    try {
      video.preload = 'auto';
      video.load();
    } catch (error) {
      done();
    }

    window.setTimeout(done, 12000);
  });
}

function collectImageSources(scope) {
  var sources = [];
  var seen = {};

  scope.querySelectorAll('img[src]').forEach(function (img) {
    var src = img.currentSrc || img.src;

    if (!src || seen[src]) {
      return;
    }

    seen[src] = true;
    sources.push(src);
  });

  return sources;
}

/**
 * @param {object} options
 * @param {HTMLElement} options.preloaderRoot
 * @param {Document|HTMLElement} [options.scope]
 * @param {function(number): void} [options.onProgress]
 * @returns {{ promise: Promise<void>, getProgress: function(): number }}
 */
export function createCamstLoadTracker(options) {
  var preloaderRoot = options.preloaderRoot;
  var scope = options.scope || document;
  var onProgress = options.onProgress || function () {};
  var tasks = [];
  var completedWeight = 0;
  var totalWeight = 0;

  function addTask(weight, runner) {
    totalWeight += weight;
    tasks.push({
      weight: weight,
      run: runner,
    });
  }

  function notify() {
    var progress = totalWeight > 0 ? completedWeight / totalWeight : 1;
    onProgress(Math.min(1, Math.max(0, progress)));
  }

  function completeTask(weight) {
    completedWeight += weight;
    notify();
  }

  collectImageSources(preloaderRoot).forEach(function (src) {
    addTask(1, function () {
      return loadImage(src).then(function () {
        completeTask(1);
      });
    });
  });

  var headerRoot = scope.querySelector('[data-camst-header]');

  if (headerRoot) {
    collectImageSources(headerRoot).forEach(function (src) {
      addTask(1, function () {
        return loadImage(src).then(function () {
          completeTask(1);
        });
      });
    });
  }

  scope.querySelectorAll('[data-camst-hero] img[src]').forEach(function (img) {
    var src = img.currentSrc || img.src;

    if (!src) {
      return;
    }

    addTask(1, function () {
      return loadImage(src).then(function () {
        completeTask(1);
      });
    });
  });

  var heroVideo = scope.querySelector('[data-camst-hero-video]');

  if (heroVideo) {
    addTask(4, function () {
      return loadVideoElement(heroVideo).then(function () {
        completeTask(4);
      });
    });
  }

  if (document.fonts && document.fonts.ready) {
    addTask(2, function () {
      return document.fonts.ready.then(function () {
        completeTask(2);
      });
    });
  }

  if (document.readyState !== 'complete') {
    addTask(2, function () {
      return new Promise(function (resolve) {
        window.addEventListener(
          'load',
          function () {
            completeTask(2);
            resolve();
          },
          { once: true }
        );
      });
    });
  } else {
    addTask(1, function () {
      return Promise.resolve().then(function () {
        completeTask(1);
      });
    });
  }

  return {
    getProgress: function () {
      return totalWeight > 0 ? completedWeight / totalWeight : 1;
    },
    start: function () {
      notify();
      return Promise.all(tasks.map(function (task) {
        return task.run();
      })).then(function () {
        onProgress(1);
      });
    },
  };
}
